// validate.js — one set of limits for every way a set gets into Lift Log:
// the keypad (active.jsx MAX_LB / MAX_REPS), the CSV / JSON importers and the
// load path (normalizeState). Weights are stored in lb.
//
//   checkSet(raw, { timed })   strict (importers): { ok, set, reason, warn }
//   sanitizeSet(st)            lenient (load / restore / sync): returns `st`
//                              itself when it is already valid, else a fixed copy
//
// Limits: 0 ≤ w ≤ 1500 lb, integer reps 1–100 (timed holds stored as reps: up
// to 3600 s; cardio: reps 0 with a distance or a duration), RPE 1–10 or null,
// distance ≤ 1,000 km, duration ≤ 24 h, body-weight snapshot ≤ 1500 lb.

export const LIMITS = Object.freeze({ maxLb: 1500, maxReps: 100, maxSec: 3600, maxDistM: 1e6, maxDurS: 86400, maxBwLb: 1500 });

const SET_TYPES = new Set(['normal', 'warmup', 'drop', 'failure', 'mini', 'myo', 'restpause', 'cluster']);

const fin = (v) => (typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN));
const round2 = (n) => Math.round(n * 100) / 100;

/** A set timestamp as epoch ms (numbers, numeric strings, ISO strings), or null. */
export function toEpochMs(v) {
  if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : null;
  if (typeof v !== 'string' || !v.trim()) return null;
  const s = v.trim();
  if (/^\d{10,14}(\.\d+)?$/.test(s)) { const n = Number(s); return n < 1e11 ? n * 1000 : n; } // seconds or ms
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

/**
 * Strict check for imported rows. raw = { w, reps, rpe?, dist?, dur?, bw?, type? }
 * (numbers or numeric strings; w already in lb). opts.timed: reps are seconds.
 */
export function checkSet(raw, opts = {}) {
  const w = fin(raw.w);
  const reps = raw.reps == null || raw.reps === '' ? 0 : fin(raw.reps);
  const dist = raw.dist == null || raw.dist === '' ? 0 : fin(raw.dist);
  const dur = raw.dur == null || raw.dur === '' ? 0 : fin(raw.dur);
  const cardio = dist > 0 || (dur > 0 && !(reps > 0));
  if (!Number.isFinite(w)) return { ok: false, reason: 'Weight is not a number' };
  if (w < 0) return { ok: false, reason: `Negative weight (${w})` };
  if (w > LIMITS.maxLb) return { ok: false, reason: `Weight over ${LIMITS.maxLb} lb (${Math.round(w)})` };
  if (!Number.isFinite(reps) || reps < 0) return { ok: false, reason: 'Reps are not a number' };
  const maxReps = opts.timed ? LIMITS.maxSec : LIMITS.maxReps;
  if (!cardio) {
    if (reps < 1) return { ok: false, reason: 'No reps' };
    if (!Number.isInteger(reps)) return { ok: false, reason: `Reps must be a whole number (${reps})` };
    if (reps > maxReps) return { ok: false, reason: `Over ${maxReps} reps (${reps})` };
  }
  if (!Number.isFinite(dist) || dist < 0 || dist > LIMITS.maxDistM) return { ok: false, reason: 'Distance out of range' };
  if (!Number.isFinite(dur) || dur < 0 || dur > LIMITS.maxDurS) return { ok: false, reason: 'Duration out of range' };
  const out = { w: round2(w), reps: cardio ? 0 : reps };
  let warn = null;
  if (raw.rpe != null && raw.rpe !== '') {
    const r = fin(raw.rpe);
    if (Number.isFinite(r) && r >= 1 && r <= 10) out.rpe = Math.round(r * 2) / 2;
    else { out.rpe = null; warn = `RPE ${raw.rpe} ignored (allowed 1–10)`; }
  } else out.rpe = null;
  if (dist > 0) out.dist = Math.round(dist);
  if (dur > 0) out.dur = Math.round(dur);
  if (raw.bw != null && raw.bw !== '') { const b = fin(raw.bw); if (Number.isFinite(b) && b > 0 && b <= LIMITS.maxBwLb) out.bw = round2(b); }
  if (raw.type != null && raw.type !== '' && raw.type !== 'normal') { if (SET_TYPES.has(raw.type)) out.type = raw.type; }
  return { ok: true, set: out, warn };
}

function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

/** Lenient repair for stored data: never drops a set, only fixes its numbers. */
export function sanitizeSet(st) {
  if (!st || typeof st !== 'object') return st;
  const w = fin(st.w), reps = fin(st.reps), setIdx = fin(st.setIdx);
  let bad = false;
  const nw = Number.isFinite(w) ? clamp(w, 0, LIMITS.maxLb) : 0;
  if (nw !== st.w) bad = true;
  let nr = Number.isFinite(reps) ? clamp(reps, 0, LIMITS.maxSec) : 0;
  if (nr !== Math.round(nr)) nr = Math.round(nr);
  if (nr !== st.reps) bad = true;
  const ni = Number.isFinite(setIdx) && setIdx >= 0 ? Math.floor(setIdx) : 0;
  if (ni !== st.setIdx) bad = true;
  let rpe = st.rpe;
  if (rpe != null) { const r = fin(rpe); rpe = Number.isFinite(r) && r >= 1 && r <= 10 ? r : null; if (rpe !== st.rpe) bad = true; }
  const fix = {};
  for (const [k, max] of [['dist', LIMITS.maxDistM], ['dur', LIMITS.maxDurS], ['bw', LIMITS.maxBwLb]]) {
    if (st[k] == null) continue;
    const v = fin(st[k]);
    const nv = Number.isFinite(v) && v > 0 ? clamp(v, 0, max) : undefined;
    if (nv !== st[k]) { bad = true; fix[k] = nv; }
  }
  let ts = st.ts;
  if (ts != null && typeof ts !== 'number') { ts = toEpochMs(ts); bad = true; }
  if (!bad) return st;
  const out = { ...st, w: nw, reps: nr, setIdx: ni, ...fix };
  if (st.rpe !== undefined) out.rpe = rpe;
  if (st.ts !== undefined) { if (ts != null) out.ts = ts; else delete out.ts; }
  for (const k of Object.keys(fix)) if (fix[k] === undefined) delete out[k];
  return out;
}
